import { useState } from "react";
import { Clear as ClearIcon } from "../../_core/components/icons";
import { makeStyles, UIBox, UIIconButton } from "../../_core/components";
import Main from "../../_core/components/layout/Main";
import UITable from "../../_core/components/ui/UITable";
import UISearch from "../../_core/components/ui/UISearch";
import UICopyIconButton from "../../_core/components/ui/UICopyIconButton";
import { useApiAlerts, useTableControls } from "../../_core/hooks";
import { formatDate } from "../../_core/utils";
import { useBillingAlertFailures } from "../hooks";

const CopyCell = ({ value }: { value: string }) => (
  <UIBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    {value}
    <UICopyIconButton text={value} />
  </UIBox>
);

const CopyColumnHeader = ({ label, values }: { label: string; values: string[] }) => (
  <UIBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    {label}
    <UICopyIconButton
      text={values.join("\n")}
      tooltip={`Copy all ${values.length} Alert IDs`}
      disabled={values.length === 0}
    />
  </UIBox>
);

const BillingAlertFailures = () => {
  const classes = useStyles();
  const controls = useTableControls();

  const [accountCodeFilter, setAccountCodeFilter] = useState("");

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountCodeFilter(e.target.value);
    controls.setPage(0);
  };

  const { data, isFetching, isError, error } = useBillingAlertFailures({
    page: controls.page,
    size: controls.rowsPerPage,
    accountCode: accountCodeFilter || undefined,
  });

  useApiAlerts(undefined, error, isError);

  return (
    <Main title="Billing Alert Failures" data-testid="billing-alert-failures-container">
      <div className={classes.root}>
        <UIBox className={classes.filterSection}>
          <UIBox className={classes.searchContainer}>
            <UISearch
              value={accountCodeFilter}
              onChange={onSearchChange}
              placeholder="Filter by exact account code..."
              className={classes.searchInput}
              debounceMs={500}
            />
            {accountCodeFilter && (
              <UIIconButton
                onClick={() => setAccountCodeFilter("")}
                size="small"
                className={classes.clearButton}
                title="Clear filter"
              >
                <ClearIcon fontSize="small" />
              </UIIconButton>
            )}
          </UIBox>
        </UIBox>

        <UITable
          className={classes.table}
          isLoading={isFetching}
          columns={[
            {
              name: "alertId",
              label: "Alert ID",
              options: {
                customHeadLabelRender: () => (
                  <CopyColumnHeader
                    label="Alert ID"
                    values={data?.content?.map((r) => String(r.alertId)) ?? []}
                  />
                ),
              },
            },
            "Account Code",
            "Alert Name",
            "Alert Level",
            "Modified At",
            "Request Status",
            "Failed Status Reason",
          ]}
          data={data?.content}
          rowCallback={(row) => [
            <CopyCell key={row.alertId} value={row.alertId} />,
            row.accountCode,
            row.alertName,
            row.alertLevel,
            formatDate(row.modifiedAt),
            row.requestStatus,
            row.failedStatusReason,
          ]}
          options={{
            ...controls,
            count: data?.totalElements,
          }}
        />
      </div>
    </Main>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    paddingTop: 8,
  },
  filterSection: {
    marginBottom: 24,
  },
  searchContainer: {
    position: "relative",
    maxWidth: 400,
  },
  searchInput: {
    width: "100%",
    paddingRight: 40,
  },
  clearButton: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
  },
  table: {
    paddingTop: 22,
  },
}));

export default BillingAlertFailures;
